// A deck, played: five headlines, one after another, a bankroll carried through, the globe turned
// to where each one broke. Then the score, and a line to share.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "../components/Link";
import { Globe } from "../globe/Globe";
import type { GlobePoint } from "../globe/types";
import { dollars } from "../../shared/game/money.ts";
import { deckResult, MARGIN_CENTS, markOf, record, shareText, START_CENTS, usePlays, type Play as PlayRecord } from "./record";
import type { Round } from "./rounds";
import { boardKey, cleanName, playerId, savedName, saveName, submitScore, useBoard, type Board } from "./board";
import { Stage, type Archive, type Leverage, type Outcome } from "./Stage";
import "./game.css";

type Props = { rounds: Round[]; deck: "today" | "fresh"; day: number; archive?: Archive | undefined };

export function Play({ rounds, deck, day, archive }: Props) {
  const all = usePlays();
  // today's paper resumes where it was left; a finished one shows its score
  const done = useMemo(() => (deck === "today" ? (deckResult(all, day)?.plays ?? []) : []), [all, deck, day]);
  const [index, setIndex] = useState(() => (deck === "today" ? Math.min(done.length, rounds.length) : 0));
  const [session, setSession] = useState<PlayRecord[]>([]);
  const [leverage, setLeverage] = useState<Leverage>(50);

  const played = deck === "today" ? done : session;
  const bankroll = played.reduce((sum, play) => sum + BigInt(play.pnl), START_CENTS);
  const round = rounds[index];

  const finish = useCallback(
    (outcome: Outcome) => {
      if (!round) return;
      const play: PlayRecord = {
        round: round.id,
        deck,
        day: deck === "today" ? day : null,
        side: outcome.side,
        leverage: outcome.leverage,
        closedAt: outcome.closedAt,
        pnl: outcome.pnl.toString(),
        busted: outcome.busted,
        category: round.category,
        at: Date.now(),
      };
      record(play);
      if (deck === "fresh") setSession((held) => [...held, play]);
      setIndex((at) => at + 1);
    },
    [round, deck, day],
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [index]);

  const points: GlobePoint[] = useMemo(
    () =>
      round?.place
        ? [{ id: round.id, lat: round.place.lat, lon: round.place.lon, weight: 0.7, tone: "warn", label: round.place.name }]
        : [],
    [round],
  );

  if (!round) return <Summary rounds={rounds} plays={played} bankroll={bankroll} deck={deck} day={day} />;

  return (
    <main className="gm">
      <header className="gm-top">
        <span className="gm-count num">
          {index + 1}/{rounds.length}
        </span>
        <span className="gm-dots" aria-hidden>
          {rounds.map((r, i) => (
            <span key={r.id} className={`gm-dot ${i < played.length ? "is-done" : i === index ? "is-now" : ""}`}>
              {i < played.length && played[i] ? markOf(played[i] as PlayRecord) : ""}
            </span>
          ))}
        </span>
        <span className="gm-bank num">{dollars(bankroll)}</span>
      </header>
      <div className="gm-place">
        <Globe points={points} selected={round.id} quake={0.55} height="100%" />
        {round.place && <span className="gm-place-name">{round.place.name}</span>}
      </div>
      <Stage key={round.id} round={round} last={index === rounds.length - 1} leverage={leverage} margin={MARGIN_CENTS} archive={archive} onLeverage={setLeverage} onDone={finish} />
    </main>
  );
}

/** "Better than 73% of 412 players today", or how it stands when it is early. */
function standing(board: Board): string | null {
  if (!board.you) return null;
  if (board.of <= 1) return "First on today's board";
  const share = Math.round((board.you.beat / (board.of - 1)) * 100);
  return `#${board.you.rank} of ${board.of} today${board.you.beat > 0 ? ` · better than ${share}%` : ""}`;
}

/** Today's board: posts the paper once, under a name chosen once. */
function TodayBoard({ day, plays, onStanding }: { day: number; plays: PlayRecord[]; onStanding: (line: string | null) => void }) {
  const queryClient = useQueryClient();
  const board = useBoard(day, true);
  const [name, setName] = useState(savedName);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const posted = board.data?.you ?? null;

  const post = useCallback(
    async (chosen: string) => {
      const clean = cleanName(chosen);
      if (!clean) {
        setError("A name of 1 to 16 letters or numbers.");
        return;
      }
      setPosting(true);
      setError(null);
      try {
        const decisions = plays.map((play) => ({ side: play.side, leverage: play.leverage, closedAt: play.closedAt ?? null }));
        const result = await submitScore(day, { player: playerId(), name: clean, decisions });
        saveName(clean);
        queryClient.setQueryData(boardKey(day), result);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "The board did not answer.");
      } finally {
        setPosting(false);
      }
    },
    [day, plays, queryClient],
  );

  // a returning player's name posts by itself
  const tried = useRef(false);
  useEffect(() => {
    if (tried.current || !board.data || board.data.you || !savedName()) return;
    tried.current = true;
    void post(savedName());
  }, [board.data, post]);

  const line = board.data ? standing(board.data) : null;
  useEffect(() => onStanding(line), [line, onStanding]);

  if (board.isError || !board.data) return null;
  return (
    <section className="gm-board">
      {line && <div className="gm-standing">{line}</div>}
      {!posted && (
        <form
          className="gm-post"
          onSubmit={(event) => {
            event.preventDefault();
            void post(name);
          }}
        >
          <input className="gm-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name for the board" maxLength={16} aria-label="Your name for the board" autoComplete="nickname" />
          <button type="submit" className="gm-cta" disabled={posting}>
            {posting ? "Posting" : "Post"}
          </button>
        </form>
      )}
      {error && <div className="gm-board-error">{error}</div>}
      {board.data.top.length > 0 && (
        <ol className="gm-top10">
          {board.data.top.slice(0, 5).map((row, i) => (
            <li key={`${row.name}-${i}`} className={posted && posted.rank === i + 1 ? "is-you" : ""}>
              <span className="num">{i + 1}</span>
              <span className="gm-top10-name">{row.name}</span>
              <span className="gm-top10-marks">{row.marks}</span>
              <span className="num">{dollars(BigInt(row.bankroll))}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Summary({ rounds, plays, bankroll, deck, day }: { rounds: Round[]; plays: PlayRecord[]; bankroll: bigint; deck: "today" | "fresh"; day: number }) {
  const [copied, setCopied] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  const up = bankroll >= START_CENTS;
  const text = deck === "today" ? shareText(day, { plays, bankroll }, line) : null;
  const share = async () => {
    if (!text) return;
    try {
      if (navigator.share) await navigator.share({ text });
      else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
      }
    } catch {
      // dismissed
    }
  };
  const tradable = rounds.find((round) => round.symbol === "BTC" || round.symbol === "ETH")?.symbol ?? "BTC";
  return (
    <main className="gm gm-summary">
      <div className="gm-summary-kicker num">{deck === "today" ? `Today's paper #${day}` : "Fresh from the wire"}</div>
      <div className={`gm-summary-bank num ${up ? "up" : "down"}`}>{dollars(bankroll)}</div>
      <div className="gm-summary-from num">from {dollars(START_CENTS)}</div>
      <div className="gm-summary-marks" aria-label="Your rounds">
        {plays.map((play, i) => (
          <span key={`${play.round}-${i}`}>{markOf(play)}</span>
        ))}
      </div>
      {deck === "today" && plays.length >= rounds.length && <TodayBoard day={day} plays={plays} onStanding={setLine} />}
      <div className="gm-summary-actions">
        {text && (
          <button type="button" className="gm-cta" onClick={share}>
            {copied ? "Copied" : "Share"}
          </button>
        )}
        <Link href="/survival" className="gm-secondary">
          Survival
        </Link>
      </div>
      <Link href={`/trade/${tradable}`} className="gm-live">
        Trade {tradable} live on papertrade →
      </Link>
    </main>
  );
}
